import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, GripVertical, Link as LinkIcon, ChevronRight } from "lucide-react";
import api from "../../lib/api";
import { AdminHeader, Btn, Input, Select } from "../../components/admin/Fields";

/* Curated shortcuts to internal routes so admin can quickly insert one
   without having to type/remember the path. */
const ROUTE_PRESETS = [
  { url: "/", label: "Ana Sayfa" },
  { url: "/about", label: "Hakkımızda" },
  { url: "/governance", label: "Kurumsal Yönetim" },
  { url: "/companies", label: "Şirketler" },
  { url: "/projects", label: "Projeler" },
  { url: "/media", label: "Medya" },
  { url: "/careers", label: "Kariyer" },
  { url: "/careers/is-ortagim-programi", label: "İş Ortağım Programı" },
  { url: "/contact", label: "İletişim" },
  { url: "/yenisehir", label: "İstanbul Yenişehir" },
  { url: "/yenisehir-projeleri", label: "Yenişehir Projeleri" },
  { url: "/realestate", label: "Gayrimenkul" },
];

const empty = () => ({
  label: { tr: "", en: "", ar: "" },
  url: "/",
  parent_id: "",
  order: 0,
  target: "_self",
  show_in: "header",
  visible: true,
  i18n_key: "",
});

/* Renders a single row given the item + its depth. Non-recursive on purpose —
   babel-loader chokes on a recursive JSX component; we flatten the tree
   below via `flattenTree()` and map over that flat list instead. */
const NavItemRow = ({ item, depth, hasChildren, tree, onEdit, onNew, onRemove, onMoveUp, onMoveDown }) => {
  const isChild = depth > 0;
  return (
    <div
      className={`flex items-center gap-2 py-2.5 pr-3 border-b border-black/5 hover:bg-brand-light/50 transition-colors ${!item.visible ? "opacity-50" : ""}`}
      data-testid={`nav-row-${item.id}`}
      style={{ paddingLeft: depth * 24 + 12 }}
    >
      {isChild ? <ChevronRight size={12} className="text-brand-medium flex-shrink-0" /> : <GripVertical size={14} className="text-brand-medium/50 flex-shrink-0" />}
      <div className="flex-1 min-w-0">
        <div className="font-medium text-brand-navy truncate">{item.label?.tr || item.label?.en || "(başlıksız)"}</div>
        <div className="text-xs text-brand-medium flex items-center gap-1 truncate">
          <LinkIcon size={10} /> {item.url}
          {item.target === "_blank" ? <span className="ml-1 text-[9px] uppercase tracking-widest text-brand-primary">↗ yeni sekme</span> : null}
          {!item.visible ? <span className="ml-1 text-[9px] uppercase tracking-widest text-red-600">gizli</span> : null}
          {hasChildren ? <span className="ml-1 text-[9px] uppercase tracking-widest text-brand-medium">{(tree[item.id] || []).length} alt öğe</span> : null}
        </div>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <button onClick={() => onMoveUp(item)} className="p-1.5 text-brand-medium hover:text-brand-navy" title="Yukarı" data-testid={`up-${item.id}`}>▲</button>
        <button onClick={() => onMoveDown(item)} className="p-1.5 text-brand-medium hover:text-brand-navy" title="Aşağı" data-testid={`down-${item.id}`}>▼</button>
        {!isChild ? (
          <button onClick={() => onNew(item.id)} className="text-xs px-2 py-1 border border-brand-navy/20 hover:border-brand-navy text-brand-navy" data-testid={`add-child-${item.id}`}>+ alt öğe</button>
        ) : null}
        <button onClick={() => onEdit(item)} className="text-xs px-3 py-1 border border-black/10 hover:border-brand-navy" data-testid={`edit-${item.id}`}>Düzenle</button>
        <button onClick={() => onRemove(item)} className="text-red-600 hover:text-red-700 p-1.5" data-testid={`del-${item.id}`}><Trash2 size={13} strokeWidth={1.5} /></button>
      </div>
    </div>
  );
};

/* Iterative DFS that produces [{ item, depth }] for rendering. */
const flattenTree = (tree, parentKey, depth, acc) => {
  const list = tree[parentKey] || [];
  for (const item of list) {
    acc.push({ item, depth });
    flattenTree(tree, item.id, depth + 1, acc);
  }
  return acc;
};

const NavMenuPage = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [busy, setBusy] = useState(false);
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setL = (lang, v) => setForm((f) => ({ ...f, label: { ...f.label, [lang]: v } }));

  const load = () => api.get("/admin/nav-items").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const parentOptions = useMemo(() => (
    [{ value: "", label: "— Üst Seviye (Ana Menü) —" }].concat(
      items.filter((i) => !i.parent_id && i.id !== editing?.id).map((i) => ({
        value: i.id,
        label: (i.label?.tr || i.label?.en || i.url).slice(0, 60),
      }))
    )
  ), [items, editing?.id]);

  // Build a tree of items for the visual tree view
  const tree = useMemo(() => {
    const byParent = {};
    items.forEach((i) => {
      const p = i.parent_id || "_root";
      (byParent[p] = byParent[p] || []).push(i);
    });
    Object.values(byParent).forEach((arr) => arr.sort((a, b) => (a.order || 0) - (b.order || 0)));
    return byParent;
  }, [items]);

  const startNew = (parentId = "") => {
    setEditing({ new: true });
    setForm({ ...empty(), parent_id: parentId });
  };
  const startEdit = (row) => {
    setEditing(row);
    setForm({
      ...empty(),
      ...row,
      label: { tr: "", en: "", ar: "", ...(row.label || {}) },
      parent_id: row.parent_id || "",
    });
  };
  const cancel = () => setEditing(null);

  const save = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, order: parseInt(form.order, 10) || 0 };
      if (editing?.new) await api.post("/admin/nav-items", payload);
      else await api.put(`/admin/nav-items/${editing.id}`, payload);
      toast.success("Kaydedildi");
      cancel();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    const label = row.label?.tr || row.url;
    if (!window.confirm(`"${label}" menü öğesi silinsin mi?\n(Alt öğeleri varsa üst seviyeye taşınır.)`)) return;
    try {
      await api.delete(`/admin/nav-items/${row.id}`);
      toast.success("Silindi");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };

  const moveUp = async (row) => {
    const siblings = tree[row.parent_id || "_root"] || [];
    const idx = siblings.findIndex((s) => s.id === row.id);
    if (idx <= 0) return;
    const prev = siblings[idx - 1];
    await Promise.all([
      api.put(`/admin/nav-items/${row.id}`, { ...row, order: prev.order }),
      api.put(`/admin/nav-items/${prev.id}`, { ...prev, order: row.order }),
    ]);
    load();
  };
  const moveDown = async (row) => {
    const siblings = tree[row.parent_id || "_root"] || [];
    const idx = siblings.findIndex((s) => s.id === row.id);
    if (idx < 0 || idx >= siblings.length - 1) return;
    const next = siblings[idx + 1];
    await Promise.all([
      api.put(`/admin/nav-items/${row.id}`, { ...row, order: next.order }),
      api.put(`/admin/nav-items/${next.id}`, { ...next, order: row.order }),
    ]);
    load();
  };

  const flatRows = useMemo(() => flattenTree(tree, "_root", 0, []), [tree]);

  return (
    <div data-testid="admin-nav-menu-page">
      <AdminHeader
        eyebrow="Menü"
        title="Site Navigasyonu"
        action={<Btn onClick={() => startNew("")} testId="new-nav-item"><Plus size={14} strokeWidth={1.5} /> Yeni Menü Öğesi</Btn>}
      />
      <p className="text-sm text-brand-medium mb-6 max-w-3xl">
        Sitenin üst menüsünü buradan yönetin. Bir öğeyi başka bir öğenin altına yerleştirmek için &quot;Üst Öğe&quot; alanını kullanın (örneğin <strong>Yenişehir</strong> öğesini <strong>Ana Sayfa</strong> altına ekleyebilirsiniz). Sıralamayı ▲▼ okları ile değiştirin.
      </p>

      <div className="bg-white border border-black/10">
        <div className="px-4 py-3 bg-brand-light/60 border-b border-black/10 text-[0.68rem] tracking-[0.24em] uppercase text-brand-medium flex items-center justify-between">
          <span>Menü Ağacı ({items.length} öğe)</span>
          <span className="text-brand-medium">▲▼ ile sırala · &quot;+ alt öğe&quot; ile iç içe yerleştir</span>
        </div>
        {flatRows.length === 0 ? (
          <div className="p-10 text-center text-brand-medium text-sm">Henüz menü öğesi yok.</div>
        ) : (
          flatRows.map(({ item, depth }) => (
            <NavItemRow
              key={item.id}
              item={item}
              depth={depth}
              hasChildren={(tree[item.id] || []).length > 0}
              tree={tree}
              onEdit={startEdit}
              onNew={startNew}
              onRemove={remove}
              onMoveUp={moveUp}
              onMoveDown={moveDown}
            />
          ))
        )}
      </div>

      {editing ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="nav-editor-modal">
          <div className="absolute inset-0 bg-black/40" onClick={cancel} />
          <form onSubmit={save} className="relative bg-white w-full max-w-2xl max-h-[92vh] shadow-2xl overflow-y-auto">
            <div className="sticky top-0 z-10 px-6 py-4 border-b border-black/10 flex items-center justify-between bg-white">
              <div>
                <div className="text-[0.6rem] tracking-[0.28em] uppercase text-brand-primary font-medium">Menü Öğesi</div>
                <div className="font-serif text-lg text-brand-navy">{editing?.new ? "Yeni" : (form.label?.tr || form.url)}</div>
              </div>
              <div className="flex items-center gap-3">
                <Btn type="submit" testId="save-nav-btn" disabled={busy}>{busy ? "Kaydediliyor…" : "Kaydet"}</Btn>
                <button type="button" onClick={cancel} className="text-brand-medium hover:text-brand-navy text-xl">×</button>
              </div>
            </div>

            <div className="p-6 space-y-5">
              {/* Multilingual labels */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input label="Başlık (TR)" value={form.label.tr} onChange={(v) => setL("tr", v)} required testId="nav-label-tr" />
                <Input label="Title (EN)" value={form.label.en} onChange={(v) => setL("en", v)} testId="nav-label-en" />
                <Input label="العنوان (AR)" value={form.label.ar} onChange={(v) => setL("ar", v)} testId="nav-label-ar" />
              </div>

              {/* URL */}
              <div>
                <div className="text-[0.68rem] tracking-[0.24em] uppercase text-brand-medium mb-2">URL / Yol</div>
                <input
                  type="text"
                  value={form.url}
                  onChange={(e) => setF("url", e.target.value)}
                  required
                  placeholder="/yenisehir"
                  className="w-full bg-white border border-black/15 focus:border-brand-navy focus:outline-none px-3 py-2 text-sm"
                  data-testid="nav-url"
                />
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {ROUTE_PRESETS.map((p) => (
                    <button
                      key={p.url}
                      type="button"
                      onClick={() => setF("url", p.url)}
                      className={`text-[0.6rem] tracking-[0.16em] uppercase px-2 py-1 border transition-colors ${form.url === p.url ? "bg-brand-navy text-white border-brand-navy" : "border-black/15 text-brand-medium hover:border-brand-navy hover:text-brand-navy"}`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                <div className="mt-2 text-xs text-brand-medium">Bu sayfa iç bir sayfa ise <code>/yenisehir</code> gibi yol yazın. Dış link için tam URL yazın (<code>https://...</code>).</div>
              </div>

              {/* Parent + order + target */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Select label="Üst Öğe" value={form.parent_id || ""} onChange={(v) => setF("parent_id", v)} options={parentOptions} testId="nav-parent" />
                <Input label="Sıra" type="number" value={form.order} onChange={(v) => setF("order", v)} testId="nav-order" />
                <Select
                  label="Hedef"
                  value={form.target}
                  onChange={(v) => setF("target", v)}
                  options={[
                    { value: "_self", label: "Aynı sekmede aç" },
                    { value: "_blank", label: "Yeni sekmede aç" },
                  ]}
                  testId="nav-target"
                />
              </div>

              {/* Visibility + show_in */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Görünüm Yeri"
                  value={form.show_in}
                  onChange={(v) => setF("show_in", v)}
                  options={[
                    { value: "header", label: "Üst menü (Header)" },
                    { value: "footer", label: "Alt menü (Footer)" },
                    { value: "both", label: "Her ikisi" },
                  ]}
                  testId="nav-show-in"
                />
                <label className="flex items-center gap-2 text-sm mt-8">
                  <input type="checkbox" checked={!!form.visible} onChange={(e) => setF("visible", e.target.checked)} data-testid="nav-visible" />
                  Menüde görünür
                </label>
              </div>

              {/* i18n key */}
              <Input
                label="i18n Anahtarı (opsiyonel)"
                value={form.i18n_key}
                onChange={(v) => setF("i18n_key", v)}
                placeholder="örn. nav.yenisehir"
                testId="nav-i18n-key"
              />
              <div className="text-xs text-brand-medium -mt-3">
                Boş bırakılırsa yukarıdaki çeviriler kullanılır. Bir anahtar girilirse (örn. <code>nav.home</code>) frontend, dil dosyasından bu anahtarı arar.
              </div>
            </div>

            <div className="sticky bottom-0 border-t border-black/10 bg-white px-6 py-4 flex items-center justify-end gap-3">
              <button type="button" onClick={cancel} className="text-sm text-brand-medium hover:text-brand-navy px-4 py-2">Vazgeç</button>
              <Btn type="submit" testId="save-nav-btn-2" disabled={busy}>{busy ? "Kaydediliyor…" : "Kaydet"}</Btn>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
};

export default NavMenuPage;
